import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Brain, Target, AlertTriangle, Heart, X, Sparkles, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface WellnessInsight {
  id: string;
  insight_type: string;
  title: string;
  message: string;
  severity: string;
  created_at: string;
}

export const WellnessInsights = () => {
  const [insights, setInsights] = useState<WellnessInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  const fetchInsights = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("wellness_insights")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      setInsights(data || []);
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast({ title: "Error", description: getErrorMessage(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Fetch recent data for analysis
      const [transactionsRes, moodRes, goalsRes, budgetsRes] = await Promise.all([
        supabase.from("transactions").select("*").eq("user_id", user.id).order("date", { ascending: false }).limit(50),
        supabase.from("mood_entries").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(14),
        supabase.from("financial_goals").select("*").eq("user_id", user.id),
        supabase.from("budgets").select("*").eq("user_id", user.id),
      ]);

      const context = {
        transactions: transactionsRes.data || [],
        moods: moodRes.data || [],
        goals: goalsRes.data || [],
        budgets: budgetsRes.data || [],
      };

      // Call the edge function for AI analysis
      const { data, error } = await supabase.functions.invoke("wellness-analysis", {
        body: { context },
      });

      if (error) throw error;

      await fetchInsights();
      toast({
        title: "Insights generated! 🧠",
        description: "Your personalized wellness analysis is ready"
      });
    } catch (error) {
      console.error("Error generating insights:", error);
      toast({ title: "Error", description: "Failed to generate insights", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const dismissInsight = async (id: string) => {
    try {
      await supabase.from("wellness_insights").delete().eq("id", id);
      setInsights((prev) => prev.filter((i) => i.id !== id));
    } catch (error) {
      console.error("Error dismissing insight:", error);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "anomaly": return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case "goal_reminder": return <Target className="h-4 w-4 text-cyan" />;
      case "emotional": return <Heart className="h-4 w-4 text-pink" />;
      default: return <Brain className="h-4 w-4 text-primary-text" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "warning": return "border-amber-500/50 bg-amber-500/5";
      case "alert": return "border-destructive/50 bg-destructive/5";
      case "positive": return "border-income/50 bg-income/5";
      default: return "border-border";
    }
  };

  return (
    <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
      {/* Gradient top border on hover */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardHeader className="pb-3 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Brain className="h-4 w-4 text-primary-text" />
              </div>
              Wellness Insights
            </CardTitle>
            <CardDescription className="text-sm">
              AI-powered analysis of your financial & emotional patterns
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={generateInsights}
            disabled={generating}
            className="border-primary/30 hover:border-primary/50 hover:bg-primary/10"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2 text-primary-text" />
            )}
            Analyze
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <AnimatePresence>
          {insights.map((insight) => (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-sm ${getSeverityColor(insight.severity)}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  {getIcon(insight.insight_type)}
                  <div>
                    <p className="text-sm font-medium">{insight.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{insight.message}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => dismissInsight(insight.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs capitalize">
                  {insight.insight_type.replace("_", " ")}
                </Badge>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {insights.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-primary-text" />
            </div>
            <p className="text-sm font-medium">No insights yet</p>
            <p className="text-xs text-muted-foreground mt-1">Click "Analyze" to get personalized insights</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
